<script setup lang="ts">
import { computed, ref } from 'vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaVaultFolderPicker from '@/components/design/domain/fileVault/GaiaVaultFolderPicker.vue';
import type { VaultFolderPickerLocation } from '@/components/design/domain/fileVault/vaultFolderPicker';
import { gaiaFontWeight, gaiaNeutral } from '@/components/design/themes/tokens';
import { useVaultStore, type VaultItem } from '@/stores/vaultStore';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { extractErrorDetail } from '@/utils/errorHandler';
import { describeVaultCounts, groupVaultKeys, type VaultUidGroups } from '@/utils/vaultSelection';

const ITEM_NOUN: Record<VaultItem['type'], string> = {
  file: 'file',
  contract: 'contract',
  directory: 'folder',
};

const props = defineProps<{
  show: boolean;
  /** The items to move — a single item (per-row action) or the whole selection (bulk). */
  items: VaultItem[];
  /** The UID of the directory the items currently live in — used to disable "Move here" when it's a no-op. Pass null when unknown (e.g. search results spanning multiple folders). */
  currentParentUid: string | null;
}>();

const emit = defineEmits<{
  'update:show': [value: boolean];
  moved: [];
}>();

const vaultStore = useVaultStore();
const message = useGaiaToast();

/** The folder the picker is in — where the move lands. */
const location = ref<VaultFolderPickerLocation | null>(null);
const moving = ref(false);
const errorMessage = ref<string>('');

// A contract moves with all its files.
const movingGroups = computed<VaultUidGroups>(() => groupVaultKeys(props.items.map(i => i.key)));

const movingDirectoryUids = computed<string[]>(() => movingGroups.value.directoryUids);

const modalTitle = computed<string>(() => {
  if (props.items.length === 1) return `Move “${props.items[0].name}”`;
  if (props.items.length > 1) return `Move ${props.items.length} items`;
  return 'Move';
});

const selectionSummary = computed<string | null>(() => {
  if (props.items.length <= 1) return null;
  return describeVaultCounts({
    files: movingGroups.value.fileUids.length,
    contracts: movingGroups.value.contractUids.length,
    folders: movingGroups.value.directoryUids.length,
  });
});

const isInvalidTarget = computed(() => {
  const target = location.value;
  if (!target) return true;
  // No-op: already in this folder
  if (target.uid === props.currentParentUid) return true;
  // Can't move a directory into itself
  if (movingDirectoryUids.value.includes(target.uid)) return true;
  return false;
});

const invalidReason = computed<string | null>(() => {
  const target = location.value;
  if (!target) return null;
  if (target.uid === props.currentParentUid)
    return props.items.length === 1
      ? `This ${ITEM_NOUN[props.items[0].type]} is already in this folder.`
      : 'The selected items are already in this folder.';
  if (movingDirectoryUids.value.includes(target.uid))
    return 'You cannot move a folder into itself.';
  return null;
});

/**
 * The target's name, read through a computed rather than `location.label` in
 * the slot: the picker emits null from a pre-flush watcher (on reset or a
 * folder change), and the GaiaText slot can re-render in that same flush
 * before the `v-else-if` drops it.
 */
const targetLabel = computed<string | null>(() => location.value?.label ?? null);

function handleLocation(value: VaultFolderPickerLocation | null): void {
  location.value = value;
  errorMessage.value = '';
}

async function handleMove(): Promise<void> {
  const target = location.value;
  if (props.items.length === 0 || !target || isInvalidTarget.value) return;

  moving.value = true;
  errorMessage.value = '';
  try {
    await vaultStore.bulkMoveVaultItems(movingGroups.value, target.uid);
    message.success(
      props.items.length === 1
        ? `Moved "${props.items[0].name}" to ${target.label}`
        : `Moved ${props.items.length} items to ${target.label}`
    );
    emit('moved');
    // Clear `moving` before calling close() — the close() guard bails out
    // while a move is in progress (preventing the user from dismissing
    // mid-request), and the `finally` reset only runs after this line, so
    // close() would otherwise see moving=true and skip the dismiss.
    moving.value = false;
    close();
  } catch (error) {
    errorMessage.value = extractErrorDetail(error, 'Failed to move item(s).');
  } finally {
    moving.value = false;
  }
}

function close(): void {
  if (moving.value) return;
  emit('update:show', false);
}

/**
 * Inline custom properties on the modal card rather than `v-bind()`. This
 * component's root is a Teleport, so Vue has no root element in place to
 * write `v-bind()` vars onto and every colour bound that way resolved to
 * nothing. On the card they cascade to header, body and footer alike.
 */
const styleVars = {
  '--move-current-weight': String(gaiaFontWeight.semibold),
  '--move-ink-color': gaiaNeutral.ink,
};
</script>

<template>
  <gaia-modal
    :style="styleVars"
    :show="props.show"
    width="520px"
    :mask-closable="!moving"
    :closable="!moving"
    @update:show="close"
  >
    <template #header>
      <gaia-text size="subtitle" weight="semibold" tone="ink">{{ modalTitle }}</gaia-text>
    </template>

    <n-flex v-if="props.items.length > 0" vertical :size="12">
      <gaia-text size="control" tone="tertiary">
        Pick the destination folder.<template v-if="selectionSummary">
          Moving {{ selectionSummary }}.</template
        >
      </gaia-text>

      <gaia-vault-folder-picker
        :active="props.show"
        :disabled-uids="movingDirectoryUids"
        disabled-title="Cannot move a folder into itself"
        :disabled="moving"
        empty-hint="This folder has nothing inside it — “Move here” drops the selection at this level."
        @update:location="handleLocation"
      />

      <gaia-banner v-if="errorMessage" type="error">{{ errorMessage }}</gaia-banner>
      <gaia-banner v-else-if="invalidReason" type="warning">{{ invalidReason }}</gaia-banner>
      <gaia-text v-else-if="targetLabel !== null" size="control" tone="tertiary">
        Will move to
        <strong class="target">{{ targetLabel }}</strong>
      </gaia-text>
    </n-flex>

    <template #footer>
      <n-flex justify="end" :size="8">
        <gaia-button size="small" :disabled="moving" @click="close">Cancel</gaia-button>
        <gaia-button
          type="primary"
          size="small"
          :loading="moving"
          :disabled="isInvalidTarget"
          @click="handleMove"
        >
          Move here
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>

<style scoped>
.target {
  font-weight: var(--move-current-weight);
  color: var(--move-ink-color);
}
</style>
